import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LoadingState } from '@ridemaadi/ui';
import { isPublicMarketingPath } from '../features/public/publicPaths';
import { fetchSystemStatus, isAdminPath, reportClientError } from '../lib/system-status';
import { SystemErrorPage } from '../components/SystemErrorPage';

/** Block the app during maintenance mode; skip gate on admin and public marketing routes. */
export function SystemGate({ children }: { children: ReactNode }) {
  const onAdminRoute = isAdminPath();
  const onPublicMarketingRoute = isPublicMarketingPath();
  const skipGate = onAdminRoute || onPublicMarketingRoute;

  const status = useQuery({
    queryKey: ['system', 'status'],
    queryFn: fetchSystemStatus,
    enabled: !skipGate,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });

  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      reportClientError({
        kind: 'window_error',
        message: event.message || 'Unknown script error',
        stack: event.error instanceof Error ? event.error.stack : undefined,
        details: { filename: event.filename, lineno: event.lineno },
      });
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message =
        reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : 'Unhandled rejection';
      reportClientError({
        kind: 'unhandledrejection',
        message,
        stack: reason instanceof Error ? reason.stack : undefined,
      });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  if (skipGate) return <>{children}</>;

  if (status.isLoading) {
    return <LoadingState label="Checking service status…" />;
  }

  if (status.isError || (status.data && !status.data.operational)) {
    return (
      <SystemErrorPage
        reason={status.isError ? 'offline' : 'unexpected'}
        onRetry={() => status.refetch()}
      />
    );
  }

  if (status.data?.maintenanceMode) {
    return (
      <SystemErrorPage
        reason="maintenance"
        message={status.data.maintenanceBanner ?? undefined}
        onRetry={() => status.refetch()}
      />
    );
  }

  return <>{children}</>;
}
